import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { ArrowRight, Files, Trash2, Plus, Settings, Lock, ChevronDown, ChevronRight, Check, ChevronsUpDown, FolderOpen, Folder } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/budget-utils';
import type { SalesBudget, SalesCategory, ResourceBudget, RoleBudget } from '@/types/budget.types';
import type { GlobalCategory } from '@/types/category.types';
import { fetchGlobalCategories, upsertCostCentreBudget, fetchCostCentreBudgetsByProject, getDataMode } from '@/lib/data-service';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';

// ── localStorage helpers for Cost Centre mode sales budget values ─────────────
// Stores per-cost-centre monthly revenue budget values (one value per month per CC)
// NOTE: In live mode we skip localStorage entirely so that all users share the
// same API-backed data.  localStorage is only used in demo/tour mode for quick
// UI feedback.
const CC_SALES_BUDGET_STORAGE_KEY = (projectId: string, ccCode: string) =>
  `cc-sales-budget-${projectId}-${ccCode}`;

// monthlyBudgets: "YYYY-MM" → value (one entry per cost centre, not per category)
interface CcSalesBudget {
  monthlyBudgets: Record<string, number>;
}

function loadCcSalesBudget(projectId: string, ccCode: string): CcSalesBudget | null {
  if (getDataMode() === 'live') return null;
  try {
    const raw = localStorage.getItem(CC_SALES_BUDGET_STORAGE_KEY(projectId, ccCode));
    if (raw) return JSON.parse(raw) as CcSalesBudget;
  } catch { /* ignore */ }
  return null;
}

function saveCcSalesBudget(projectId: string, ccCode: string, data: CcSalesBudget) {
  if (getDataMode() === 'live') return;
  // Write-through to localStorage for immediate UI consistency
  try {
    localStorage.setItem(CC_SALES_BUDGET_STORAGE_KEY(projectId, ccCode), JSON.stringify(data));
  } catch { /* ignore */ }
}

// Debounced API persist for sales centre budgets
const ccSalesBudgetPendingSaves: Record<string, {
  timer: ReturnType<typeof setTimeout>;
  save: () => Promise<void>;
}> = {};

function persistCcSalesBudgetToApi(costCentreId: number, ccCode: string, monthly: Record<string, number>) {
  const timerKey = `sales-${ccCode}`;
  if (ccSalesBudgetPendingSaves[timerKey]) {
    clearTimeout(ccSalesBudgetPendingSaves[timerKey].timer);
  }
  const save = () => upsertCostCentreBudget(costCentreId, 'sales', monthly);
  ccSalesBudgetPendingSaves[timerKey] = {
    timer: setTimeout(() => {
      save().catch((err) => {
        console.error(`[SalesBudget] Failed to persist budget for CC ${ccCode}:`, err);
      });
      delete ccSalesBudgetPendingSaves[timerKey];
    }, 800),
    save,
  };
}

export async function flushPendingCcSalesBudgetSaves(): Promise<void> {
  const pending = Object.entries(ccSalesBudgetPendingSaves);
  if (pending.length === 0) return;
  for (const [, { timer }] of pending) {
    clearTimeout(timer);
  }
  await Promise.all(
    pending.map(([key, { save }]) => {
      delete ccSalesBudgetPendingSaves[key];
      return save().catch((err) => {
        console.error('[SalesBudget] Failed to flush pending CC budget save:', err);
      });
    })
  );
}

import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FillSheet } from './fill-sheet';
import { ShiftBudgetSheet } from './shift-budget-sheet';
import { AISuggestion } from './ai-suggestion';
import type { FillType } from './fill-dialog';
import { saveMockSalesBudgetCategories } from '@/lib/mock-budget-storage';

interface EditSalesBudgetFormTMProps {
  projectId: number;
  salesBudget: SalesBudget;
  onChange: (changes: SalesBudget) => void;
  projectStartDate?: string;
  projectEndDate?: string;
  availableCategories: SalesCategory[];
  resourceBudget?: ResourceBudget; // Resource budget data to show labour breakdown
  hasResourceBudget?: boolean; // When true: Labour row is locked (read-only). When false: Labour row is editable.
  cascadeTargetCategoryId?: number | null; // The designated cascade target category from Budget Settings
  /** Cost Centre mode — when true, renders per-cost-centre budget rows instead of category rows */
  isCostCentreMode?: boolean;
  /** Flat list of cost centres for Cost Centre mode (used when isStructureView is false) */
  costCentres?: CostCentre[];
  /** Hierarchical cost centres for Cost Centre mode structure view */
  hierarchicalCostCentres?: HierarchicalCostCentre[];
  /** When true, shows hierarchical/structure view of cost centres */
  isStructureView?: boolean;
}

// Month names for columns
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Mock data: Initial budget values for sales categories (with markup over cost)
const MOCK_INITIAL_BUDGETS: Record<string, Record<string, number>> = {
  'Labour': {
    '2024-05': 35000,
    '2024-06': 35000,
    '2024-07': 35000,
    '2024-08': 35000,
    '2024-09': 35000,
    '2024-10': 35000,
    '2024-11': 35000,
    '2024-12': 35000,
    '2025-01': 35000,
  },
  'Materials': {
    '2024-05': 6500,
    '2024-06': 3900,
    '2024-07': 2600,
    '2024-08': 2600,
    '2024-09': 1950,
    '2024-10': 1950,
    '2024-11': 1300,
    '2024-12': 1300,
    '2025-01': 650,
  },
  'Equipment': {
    '2024-05': 19500,
    '2024-06': 2600,
    '2024-07': 1300,
    '2024-08': 1300,
    '2024-09': 1300,
    '2024-10': 1300,
    '2024-11': 650,
    '2024-12': 650,
    '2025-01': 650,
  },
  'Subcontractors': {
    '2024-05': 0,
    '2024-06': 0,
    '2024-07': 12000,
    '2024-08': 12000,
    '2024-09': 12000,
    '2024-10': 12000,
    '2024-11': 0,
    '2024-12': 0,
    '2025-01': 0,
  },
  'Travel & Expenses': {
    '2024-05': 1950,
    '2024-06': 1950,
    '2024-07': 1950,
    '2024-08': 1950,
    '2024-09': 1950,
    '2024-10': 1950,
    '2024-11': 1950,
    '2024-12': 1950,
    '2025-01': 1950,
  },
  'Software & Licenses': {
    '2024-05': 6500,
    '2024-06': 650,
    '2024-07': 650,
    '2024-08': 650,
    '2024-09': 650,
    '2024-10': 650,
    '2024-11': 650,
    '2024-12': 650,
    '2025-01': 650,
  },
  'Overhead': {
    '2024-05': 3900,
    '2024-06': 3900,
    '2024-07': 3900,
    '2024-08': 3900,
    '2024-09': 3900,
    '2024-10': 3900,
    '2024-11': 3900,
    '2024-12': 3900,
    '2025-01': 3900,
  },
  'Contingency': {
    '2024-05': 6500,
    '2024-06': 6500,
    '2024-07': 6500,
    '2024-08': 6500,
    '2024-09': 6500,
    '2024-10': 6500,
    '2024-11': 6500,
    '2024-12': 6500,
    '2025-01': 6500,
  },
};

// NumberInput component that handles 2dp formatting properly with max value validation
const NumberInput = ({ value, onChange, maxValue = 999999.99, onFocus, disabled, ...props }: { value: number; onChange: (value: number) => void; maxValue?: number; onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void; disabled?: boolean; [key: string]: any }) => {
  const [displayValue, setDisplayValue] = useState(value.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    let numValue = parseFloat(displayValue) || 0;
    
    // Enforce maximum value
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    const formattedValue = parseFloat(numValue.toFixed(2));
    setDisplayValue(formattedValue.toFixed(2));
    // Only call onChange if the value actually changed to avoid false "unsaved changes"
    if (formattedValue !== value) {
      onChange(formattedValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const val = e.target.value;
    // Allow typing numbers, decimal point, and empty string
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      
      // Debounced onChange to enable toolbar buttons while typing
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new debounced timer (300ms delay to allow for slow typing)
      debounceTimerRef.current = setTimeout(() => {
        let numValue = parseFloat(val) || 0;
        // Enforce maximum value
        if (numValue > maxValue) {
          numValue = maxValue;
        }
        const formattedValue = parseFloat(numValue.toFixed(2));
        // Only call onChange if the value actually changed
        if (formattedValue !== value) {
          onChange(formattedValue);
        }
      }, 300);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
};

// Helper function to format numbers with thousand separators
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Extended category interface with monthly budgets as array for form display
interface CategoryBudgetRow extends Omit<SalesCategory, 'monthlyBudgets'> {
  monthlyBudgets: number[]; // Budget per month (array for form display, converted to/from Record for API)
}

export function EditSalesBudgetFormTM({
  projectId,
  salesBudget,
  onChange,
  projectStartDate,
  projectEndDate,
  availableCategories,
  resourceBudget,
  hasResourceBudget = false,
  cascadeTargetCategoryId,
  isCostCentreMode = false,
  costCentres = [],
  hierarchicalCostCentres = [],
  isStructureView = false,
}: EditSalesBudgetFormTMProps) {
  // ── Cost Centre mode — delegate to the dedicated view ────────────────────
  if (isCostCentreMode) {
    return (
      <SalesCostCentreBudgetView
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        projectId={String(projectId)}
        projectStartDate={projectStartDate}
        projectEndDate={projectEndDate}
        onChange={onChange}
        salesBudget={salesBudget}
      />
    );
  }

  // Labour is locked (read-only) when a resource budget exists — it is owned by the resource budget layer.
  // When no resource budget exists, Labour is freely editable.
  const isLabourEditable = !hasResourceBudget;

  // Use budget calculations hook to get rates from Rate Card (respects rate hierarchy)
  const { roleBreakdown } = useBudgetCalculations(
    projectId.toString(),
    resourceBudget || null
  );

  // Create a mapping from role name to bill rate (from Rate Card)
  // This ensures we always use the latest rates from the Rate Card
  const roleRateMap = useMemo(() => {
    const map = new Map<string, number>();
    if (roleBreakdown && roleBreakdown.length > 0) {
      roleBreakdown.forEach(role => {
        map.set(role.roleName, role.averageBillRate);
      });
      console.log('[EditSalesBudgetFormTM] Role rate map created:', Array.from(map.entries()));
    }
    return map;
  }, [roleBreakdown]);
  
  // Calculate all available months for the project across all years
  const allProjectMonths = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Show all months of current year if no project dates
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => ({ year: currentYear, month: i }));
    }
    
    const startDate = new Date(projectStartDate);
    const endDate = new Date(projectEndDate);
    
    const months: { year: number; month: number }[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      months.push({ year: current.getFullYear(), month: current.getMonth() });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  })();

  // Show ALL months for the entire project duration - no year filtering
  const yearMonths = allProjectMonths;

  // Create a mapping from global month index to year/month
  const monthIndexMap = yearMonths.map((ym, index) => ({
    globalIndex: index,
    year: ym.year,
    month: ym.month,
    yearMonthKey: `${ym.year}-${ym.month}` // Unique key for this month
  }));

  // Show ALL months - no dynamic loading
  const visibleMonths = monthIndexMap.map(m => m.globalIndex);
  
  // Total number of months to display
  const totalMonths = yearMonths.length;

  // Initialize categories with properly sized monthlyBudgets arrays
  const [categories, setCategories] = useState<CategoryBudgetRow[]>(() => {
    return availableCategories.map(category => {
      // Create monthly budgets array
      const monthlyBudgets = monthIndexMap.map(({ year, month }) => {
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
        
        // First priority: Use saved monthly budgets from API if available
        if (category.monthlyBudgets && category.monthlyBudgets[monthKey] !== undefined) {
          return category.monthlyBudgets[monthKey];
        }
        
        // Live mode: For Labour category with resource budget, calculate from resource budget
        if (getDataMode() === 'live' && (category.type === 'labour' || category.name === 'Labour')) {
          if (resourceBudget?.byRole) {
            return resourceBudget.byRole.reduce((total, role) => {
              const hours = role.monthlyHours?.[monthKey] || 0;
              const billRate = role.billRate || 0;
              return total + (hours * billRate);
            }, 0);
          }
          return 0;
        }
        
        // Demo mode fallback: Use mock initial budgets for new categories
        const categoryBudgets = MOCK_INITIAL_BUDGETS[category.name];
        return categoryBudgets?.[monthKey] || 0;
      });

      return {
        ...category,
        monthlyBudgets,
      };
    });
  });

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedCell, setSelectedCell] = useState<{
    categoryIndex: number;
    monthIndex: number | null;
  } | null>(null);

  // Derive display categories: when hasResourceBudget, filter out only the cascade target
  // category and render a single synthetic Labour group row for it instead.
  // Other labour-type categories manually added by the user remain visible and editable.
  const isCascadeTarget = useCallback(
    (c: CategoryBudgetRow) => cascadeTargetCategoryId != null && (c.id === cascadeTargetCategoryId || c.globalCategoryId === cascadeTargetCategoryId),
    [cascadeTargetCategoryId]
  );
  const labourCategories = useMemo(
    () => categories.filter(c => isCascadeTarget(c)),
    [categories, isCascadeTarget]
  );
  const displayCategories = useMemo(
    () => hasResourceBudget ? categories.filter(c => !isCascadeTarget(c)) : categories,
    [categories, hasResourceBudget, isCascadeTarget]
  );
  const showSyntheticLabour = hasResourceBudget && labourCategories.length > 0;

  // Track which Labour group is expanded to show role breakdown
  const [expandedLabourCategory, setExpandedLabourCategory] = useState<number | null>(null);
  const LABOUR_SYNTHETIC_ID = -1;
  const isLabourGroupExpanded = expandedLabourCategory === LABOUR_SYNTHETIC_ID;

  // Helper function to get the bill rate for a role from Rate Card
  // Falls back to the rate in resourceBudget if not found in Rate Card
  const getRoleBillRate = (roleName: string, fallbackRate: number): number => {
    const rateFromCard = roleRateMap.get(roleName);
    if (rateFromCard !== undefined && rateFromCard > 0) {
      return rateFromCard;
    }
    if (rateFromCard === 0) {
      console.warn('[EditSalesBudgetFormTM] Rate Card returned 0 for role:', roleName, 'using fallback:', fallbackRate);
    } else {
      console.warn('[EditSalesBudgetFormTM] No rate found in Rate Card for role:', roleName, 'using fallback:', fallbackRate);
    }
    return fallbackRate;
  };

  // Calculate labour revenue per month from resource budget (hours × billRate per role)
  const calculateLabourGroupMonthly = (): number[] => {
    if (!resourceBudget?.byRole) return Array(totalMonths).fill(0);
    const monthly = Array(totalMonths).fill(0);
    yearMonths.forEach((yearMonth, projectMonthIndex) => {
      const monthKey = `${yearMonth.year}-${String(yearMonth.month + 1).padStart(2, '0')}`;
      resourceBudget.byRole.forEach((role: RoleBudget) => {
        const hours = role.monthlyHours?.[monthKey] || 0;
        const billRate = getRoleBillRate(role.role, role.billRate);
        monthly[projectMonthIndex] += hours * billRate;
      });
    });
    return monthly;
  };

  // Get roles that have hours allocated in any month
  const getRolesWithHours = (): RoleBudget[] => {
    if (!resourceBudget?.byRole) return [];
    return resourceBudget.byRole.filter((role: RoleBudget) =>
      role.monthlyHours && Object.values(role.monthlyHours).some(h => h > 0)
    );
  };

  // Prefer the stored monthlyBudgets from the cascade target category (authoritative values
  // computed by the backend during cascade using the correct BillRate). Only fall back to
  // recalculating from resource budget roles when no stored monthly data is available.
  const labourGroupMonthly = showSyntheticLabour
    ? (labourCategories[0]?.monthlyBudgets?.some((v: number) => v > 0)
      ? [...labourCategories[0].monthlyBudgets]
      : calculateLabourGroupMonthly())
    : [];
  const labourGroupTotal = labourGroupMonthly.reduce((s, v) => s + v, 0);
  const labourRoles = getRolesWithHours();

  // Save categories to localStorage for demo mode sync
  useEffect(() => {
    const mode = getDataMode();
    if (mode === 'demo') {
      saveMockSalesBudgetCategories(projectId, categories);
    }
    // onChange is handled by the separate useEffect below that has isInitialMount guard
  }, [projectId, categories]);

  // Calculate labour revenue from resource budget (hours × bill rate per month)
  const calculateLabourRevenueFromResourceBudget = (): number[] => {
    if (!resourceBudget?.byRole) {
      return Array(totalMonths).fill(0);
    }

    const monthlyRevenue = Array(totalMonths).fill(0);
    
    // Map each project month to its YYYY-MM key and calculate revenue
    yearMonths.forEach((yearMonth, projectMonthIndex) => {
      const monthKey = `${yearMonth.year}-${String(yearMonth.month + 1).padStart(2, '0')}`;
      
      resourceBudget.byRole.forEach((role: RoleBudget) => {
        const hours = role.monthlyHours?.[monthKey] || 0;
        const billRate = getRoleBillRate(role.role, role.billRate);
        monthlyRevenue[projectMonthIndex] += hours * billRate;
      });
    });

    return monthlyRevenue;
  };



  // Fill dialog state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ categoryIndex: number; monthIndex: number; value: number } | null>(null);

  // Shift budget dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ categoryIndex: number; categoryName: string } | null>(null);

  // Available global categories for inline combobox
  const [availableGlobalCategories, setAvailableGlobalCategories] = useState<GlobalCategory[]>([]);
  const [openCategoryPopover, setOpenCategoryPopover] = useState<number | null>(null);

  // Resolve the display name for the synthetic Labour group row.
  // Uses the cascade target category name when available, otherwise falls back to "Labour".
  const cascadeTargetDisplayName = (() => {
    if (cascadeTargetCategoryId) {
      const fromCat = labourCategories.find(c => c.id === cascadeTargetCategoryId || c.globalCategoryId === cascadeTargetCategoryId);
      if (fromCat) return fromCat.name;
      const fromGlobal = availableGlobalCategories.find(gc => gc.id === cascadeTargetCategoryId);
      if (fromGlobal) return fromGlobal.name;
    }
    return 'Labour';
  })();

  // Delete confirmation state
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; category: CategoryBudgetRow; hasActuals: boolean; hasBudget: boolean } | null>(null);

  // AI suggestion state for pattern detection
  const [patternSuggestion, setPatternSuggestion] = useState<{
    categoryIndex: number;
    value: number;
    fromMonth: number;
    toMonth: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Track if this is the initial mount to prevent marking form as dirty on load
  const isInitialMount = useRef(true);

  const navigate = useNavigate();

  // Track whether any global categories exist (for empty state messaging)
  const [hasGlobalCategories, setHasGlobalCategories] = useState<boolean | null>(null);

  const goToBudgetSettings = useCallback(() => {
    navigate('/settings/general?tab=budget-settings');
  }, [navigate]);

  // Load global categories on mount for inline combobox
  useEffect(() => {
    fetchGlobalCategories().then(globalCategories => {
      const activeCategories = globalCategories.filter(gc => gc.status === 'active');
      setAvailableGlobalCategories(activeCategories);
      setHasGlobalCategories(activeCategories.length > 0);
    }).catch(() => {
      setHasGlobalCategories(false);
    });
  }, []);

  // Auto-inject the cascade target category when a Resource Budget exists and no matching category is present.
  // Uses the specific cascade target when set, otherwise falls back to any labour-type category.
  useEffect(() => {
    if (!hasResourceBudget) return;

    if (cascadeTargetCategoryId) {
      const hasTarget = categories.some(
        c => c.id === cascadeTargetCategoryId || c.globalCategoryId === cascadeTargetCategoryId
      );
      if (hasTarget) return;

      const targetGlobal = availableGlobalCategories.find(gc => gc.id === cascadeTargetCategoryId);
      if (!targetGlobal) return;

      const targetCategory: CategoryBudgetRow = {
        id: targetGlobal.id,
        name: targetGlobal.name,
        type: targetGlobal.type,
        budget: 0,
        actual: 0,
        remaining: 0,
        percentBilled: 0,
        scope: 'project',
        isCustom: false,
        globalCategoryId: targetGlobal.id,
        monthlyBudgets: Array(totalMonths).fill(0),
      };
      setCategories(prev => [targetCategory, ...prev]);
      return;
    }

    const hasLabour = categories.some(c => c.type === 'labour');
    if (hasLabour) return;

    // Look for a labour-type global category to use; fall back to a synthesised one
    const labourGlobal = availableGlobalCategories.find(gc => gc.type === 'labour');
    const labourCategory: CategoryBudgetRow = {
      id: labourGlobal?.id ?? Date.now(),
      name: labourGlobal?.name ?? 'Labour',
      type: 'labour',
      budget: 0,
      actual: 0,
      remaining: 0,
      percentBilled: 0,
      scope: 'project',
      isCustom: false,
      globalCategoryId: labourGlobal?.id,
      monthlyBudgets: Array(totalMonths).fill(0),
    };
    setCategories(prev => [labourCategory, ...prev]);
  }, [hasResourceBudget, cascadeTargetCategoryId, availableGlobalCategories, totalMonths]);

  useEffect(() => {
    // Skip onChange on initial mount to prevent false "unsaved changes" alert
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Calculate totals for each category and convert monthlyBudgets array to Record<string, number>
    const updatedCategories = categories.map(cat => {
      // Convert monthlyBudgets array to Record<string, number> for API storage
      const monthlyBudgetsRecord: Record<string, number> = {};
      cat.monthlyBudgets.forEach((value, index) => {
        if (monthIndexMap[index]) {
          const { year, month } = monthIndexMap[index];
          const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
          monthlyBudgetsRecord[monthKey] = value;
        }
      });

      const budget = cat.monthlyBudgets.reduce((sum, val) => sum + val, 0);
      const actual = cat.actual || 0; // Use existing actual value or 0
      const remaining = budget - actual;
      const percentBilled = budget > 0 ? (actual / budget) * 100 : 0;

      return {
        ...cat,
        budget,
        actual,
        remaining,
        percentBilled,
        monthlyBudgets: monthlyBudgetsRecord,
      };
    });

    // Calculate overall totals
    const totalBudget = updatedCategories.reduce((sum, cat) => sum + cat.budget, 0);
    const totalActual = updatedCategories.reduce((sum, cat) => sum + (cat.actual || 0), 0);
    const totalRemaining = totalBudget - totalActual;
    const totalPercentBilled = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    onChange({
      ...salesBudget,
      projectType: 'time-and-materials',
      categories: updatedCategories,
      totals: {
        budget: totalBudget,
        actual: totalActual,
        remaining: totalRemaining,
        percentBilled: totalPercentBilled,
      },
    });
  }, [categories]);

  const updateMonthlyBudget = (categoryIndex: number, monthIndex: number, value: number) => {
    console.log('[updateMonthlyBudget] Called:', { categoryIndex, monthIndex, value, totalMonths });
    const updated = [...categories];
    console.log('[updateMonthlyBudget] Current category:', updated[categoryIndex]);
    
    // Ensure monthlyBudgets array is properly sized
    const existingBudgets = updated[categoryIndex].monthlyBudgets || [];
    console.log('[updateMonthlyBudget] Existing budgets:', existingBudgets);
    const monthlyBudgets = existingBudgets.length >= totalMonths 
      ? [...existingBudgets]
      : [...existingBudgets, ...Array(totalMonths - existingBudgets.length).fill(0)];
    console.log('[updateMonthlyBudget] Sized monthlyBudgets:', monthlyBudgets);
    
    monthlyBudgets[monthIndex] = value;
    console.log('[updateMonthlyBudget] After update:', monthlyBudgets);
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    
    // Pattern detection for AI suggestions
    detectPattern(categoryIndex, monthIndex, value, monthlyBudgets);
  };

  // Pattern detection for AI suggestions
  const detectPattern = (categoryIndex: number, monthIndex: number, value: number, monthlyBudgets: number[]) => {
    // Don't suggest if already dismissed
    const suggestionKey = `pattern-${categoryIndex}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    
    // Check if last 3 values are the same and non-zero
    if (monthIndex >= 2 && value > 0) {
      const last3 = [
        monthlyBudgets[monthIndex - 2],
        monthlyBudgets[monthIndex - 1],
        monthlyBudgets[monthIndex]
      ];
      
      const allSame = last3.every(v => v === value);
      
      if (allSame && monthIndex < totalMonths - 1) {
        // Show suggestion to fill remaining months
        setPatternSuggestion({
          categoryIndex,
          value,
          fromMonth: monthIndex + 1,
          toMonth: totalMonths - 1
        });
      }
    }
  };

  // Row selection handlers
  const toggleRowSelection = (categoryId: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(categoryId)) {
      newSelection.delete(categoryId);
    } else {
      newSelection.add(categoryId);
    }
    setSelectedRows(newSelection);
    // Clear cell selection when selecting a row
    setSelectedCell(null);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === displayCategories.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(displayCategories.map(cat => cat.id)));
    }
    // Clear cell selection when selecting all rows
    setSelectedCell(null);
  };

  // Fill handlers
  const handleFillClick = (categoryIndex: number, monthIndex: number) => {
    const category = categories[categoryIndex];
    const value = category.monthlyBudgets[monthIndex] || 0;
    setFillSource({ categoryIndex, monthIndex, value });
    setFillDialogOpen(true);
  };

  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;

    const { categoryIndex, monthIndex, value } = fillSource;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];

    // Determine the range based on fill direction
    const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
    const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
    const monthCount = Math.abs(endMonth - startMonth) + 1;

    // Apply fill based on type
    if (fillType === 'copy') {
      // Copy same value to all months in range
      for (let i = startMonth; i <= endMonth; i++) {
        monthlyBudgets[i] = value;
      }
    } else if (fillType === 'distribute') {
      // Distribute value equally across months
      const valuePerMonth = value / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100;
      const remainder = value - (roundedValue * monthCount);
      
      for (let i = startMonth; i <= endMonth; i++) {
        const isLast = i === endMonth;
        monthlyBudgets[i] = isLast ? roundedValue + remainder : roundedValue;
      }
    }

    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };

    setCategories(updated);
    setFillDialogOpen(false);
    setFillSource(null);
  };

  // Handle pattern suggestion acceptance
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    
    const { categoryIndex, value, fromMonth, toMonth } = patternSuggestion;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];
    
    // Fill remaining months with the pattern value
    for (let i = fromMonth; i <= toMonth; i++) {
      monthlyBudgets[i] = value;
    }
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    setPatternSuggestion(null);
  };
  
  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      const suggestionKey = `pattern-${patternSuggestion.categoryIndex}`;
      setDismissedSuggestions(new Set(dismissedSuggestions).add(suggestionKey));
    }
    setPatternSuggestion(null);
  };

  // Duplicate handlers
  const handleDuplicateCategory = (categoryIndex: number) => {
    const sourceCategory = categories[categoryIndex];
    const newCategory: CategoryBudgetRow = {
      ...sourceCategory,
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: `${sourceCategory.name} (Copy)`,
      monthlyBudgets: [...sourceCategory.monthlyBudgets],
      scope: 'project', // Duplicated categories are project-specific
      isCustom: true,
    };

    // Insert below source category
    const updated = [
      ...categories.slice(0, categoryIndex + 1),
      newCategory,
      ...categories.slice(categoryIndex + 1),
    ];

    setCategories(updated);
  };

  const handleDuplicateMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const updatedCategories = [...categories];

    // Process in reverse order to maintain correct insertion positions
    for (let i = updatedCategories.length - 1; i >= 0; i--) {
      const category = updatedCategories[i];
      if (selectedCategoryIds.includes(category.id)) {
        const newCategory: CategoryBudgetRow = {
          ...category,
          id: Date.now() + Math.floor(Math.random() * 1000) + i,
          name: `${category.name} (Copy)`,
          monthlyBudgets: [...category.monthlyBudgets],
          scope: 'project',
          isCustom: true,
        };
        updatedCategories.splice(i + 1, 0, newCategory);
      }
    }

    setCategories(updatedCategories);
    setSelectedRows(new Set());
  };

  // Shift budget handler
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;

    const categoryIndex = shiftSource.categoryIndex;
    const category = categories[categoryIndex];
    const newMonthlyBudgets = [...(category.monthlyBudgets || Array(totalMonths).fill(0))];

    // Copy values to destination
    const shiftAmount = destinationStart - sourceFrom;
    const sourceMonthlyBudgets = category.monthlyBudgets || Array(totalMonths).fill(0);
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        newMonthlyBudgets[destIndex] = sourceMonthlyBudgets[i];
      }
    }

    // Clear source if requested
    if (clearSource) {
      const destinationEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        if (i < destinationStart || i > destinationEnd) {
          newMonthlyBudgets[i] = 0;
        }
      }
    }

    const updated = [...categories];
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets: newMonthlyBudgets,
    };

    setCategories(updated);
    setShiftDialogOpen(false);
    setShiftSource(null);
  };

  // Delete handlers
  const removeCategory = (categoryIndex: number) => {
    const category = categories[categoryIndex];
    
    // Check if category has actual costs
    const hasActuals = (category.actual || 0) > 0;
    
    // Check if category has budget values
    const hasBudget = category.monthlyBudgets && category.monthlyBudgets.some(budget => budget > 0);

    // Prevent deletion if there are actual costs
    if (hasActuals) {
      setDeleteAttempt({ 
        index: categoryIndex, 
        category,
        hasActuals: true,
        hasBudget: hasBudget || false
      });
      return;
    }

    // Warn if there are budget values but allow deletion
    if (hasBudget) {
      setDeleteAttempt({ 
        index: categoryIndex, 
        category,
        hasActuals: false,
        hasBudget: true
      });
      return;
    }

    // Allow deletion of categories without costs or budget
    setCategories(categories.filter((_, i) => i !== categoryIndex));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const removeMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const categoriesToDelete = categories.filter(cat => selectedCategoryIds.includes(cat.id));

    // Check if any selected categories have actual costs or budget values
    const categoriesWithActuals = categoriesToDelete.filter(cat => cat.actual > 0);
    const categoriesWithBudget = categoriesToDelete.filter(cat => 
      cat.monthlyBudgets && cat.monthlyBudgets.some(budget => budget > 0)
    );

    // Prevent deletion if there are actual costs
    if (categoriesWithActuals.length > 0) {
      setDeleteAttempt({ 
        index: -1, 
        category: categoriesWithActuals[0],
        hasActuals: true,
        hasBudget: categoriesWithBudget.length > 0
      });
      return;
    }

    // Warn if there are budget values but allow deletion
    if (categoriesWithBudget.length > 0) {
      setDeleteAttempt({ 
        index: -1, 
        category: categoriesWithBudget[0],
        hasActuals: false,
        hasBudget: true
      });
      return;
    }

    // Allow deletion of categories without costs or budget
    setCategories(categories.filter(cat => !selectedCategoryIds.includes(cat.id)));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const cancelDeleteCategory = () => {
    setDeleteAttempt(null);
  };

  const confirmDeleteCategory = () => {
    if (!deleteAttempt) return;
    
    const selectedCategoryIds = Array.from(selectedRows);
    setCategories(categories.filter(cat => !selectedCategoryIds.includes(cat.id)));
    setSelectedRows(new Set());
    setSelectedCell(null);
    setDeleteAttempt(null);
  };

  // Add Category handler (inline row, matching Cost Budget pattern)
  const addCategory = () => {
    const usedCategoryIds = categories.map(c => c.globalCategoryId).filter(Boolean);
    const availableCategory = availableGlobalCategories.find(
      gc => !usedCategoryIds.includes(gc.id) && gc.id !== cascadeTargetCategoryId
    );

    const newCategory: CategoryBudgetRow = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: availableCategory?.name || '',
      code: availableCategory?.code,
      type: availableCategory?.type || 'expenses',
      budget: 0,
      actual: 0,
      remaining: 0,
      percentBilled: 0,
      scope: 'project',
      isCustom: !availableCategory,
      globalCategoryId: availableCategory?.id,
      monthlyBudgets: Array(totalMonths).fill(0),
    };

    setCategories([...categories, newCategory]);
  };

  const updateCategorySelection = (index: number, globalCategoryId: string) => {
    if (!globalCategoryId) return;
    const globalCategory = availableGlobalCategories.find(gc => String(gc.id) === globalCategoryId);
    if (!globalCategory) return;

    const updated = [...categories];
    updated[index] = {
      ...updated[index],
      name: globalCategory.name,
      code: globalCategory.code,
      type: globalCategory.type,
      globalCategoryId: globalCategory.id,
      isCustom: false,
    };
    setCategories(updated);
  };

  const duplicateCategoryNames = useMemo(() => {
    const names = displayCategories.map(c => c.name).filter(Boolean);
    return new Set(names.filter((name, i) => names.indexOf(name) !== i));
  }, [displayCategories]);

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
          <h2 className="text-lg font-semibold">Sales Budget by Category</h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const { categoryIndex, monthIndex } = selectedCell;
                  const category = categories[categoryIndex];
                  const value = category.monthlyBudgets?.[monthIndex] || 0;
                  // Validation: value > 0 && monthIndex < totalMonths - 1
                  if (value > 0 && monthIndex < totalMonths - 1) {
                    handleFillClick(categoryIndex, monthIndex);
                  }
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (() => {
                if (!selectedCell || selectedCell.monthIndex === null) return true;
                const { categoryIndex, monthIndex } = selectedCell;
                const category = categories[categoryIndex];
                if (!category) return true;
                const value = category.monthlyBudgets?.[monthIndex] || 0;
                return value === 0 || monthIndex === totalMonths - 1;
              })()}
              title="Fill right from selected cell"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) handleDuplicateCategory(categoryIndex);
                } else if (selectedRows.size > 1) {
                  handleDuplicateMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Duplicate ${selectedRows.size} selected categories` : "Duplicate category"}
            >
              <Files className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const categoryIndex = selectedCell.categoryIndex;
                  setShiftSource({ 
                    categoryIndex, 
                    categoryName: categories[categoryIndex].name 
                  });
                  setShiftDialogOpen(true);
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (categories[selectedCell.categoryIndex]?.monthlyBudgets?.[selectedCell.monthIndex] ?? 0) === 0}
              title="Shift budget between months (select a monthly cell first)"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Shift
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) {
                    removeCategory(categoryIndex);
                  }
                } else if (selectedRows.size > 1) {
                  removeMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Delete ${selectedRows.size} selected categories` : "Delete selected category"}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button 
              onClick={addCategory} 
              size="sm" 
              variant="outline"
              disabled={availableGlobalCategories.length > 0 && categories.filter(c => c.globalCategoryId).length >= availableGlobalCategories.length}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
        <div className="p-0">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {hasGlobalCategories === false ? (
                <div className="space-y-2">
                  <p>No budget categories configured.</p>
                  <p>
                    Please configure categories in{' '}
                    <button
                      type="button"
                      onClick={goToBudgetSettings}
                      className="text-primary underline underline-offset-4 hover:text-primary/80 inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Settings → Budget Settings
                    </button>
                  </p>
                </div>
              ) : (
                <>No categories yet. Click "Add Category" to get started.</>
              )}
            </div>
          ) : (
            <>
              {/* 3-Column Layout: Left (sticky) + Middle (scrollable) + Right (sticky) */}
              <div className="flex border rounded-md">
                {/* LEFT SECTION: Checkbox + Category (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-r">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px] sticky left-0 z-10 bg-background">
                          <Checkbox
                            checked={selectedRows.size === displayCategories.length && displayCategories.length > 0}
                            onCheckedChange={toggleAllRows}
                            aria-label="Select all rows"
                          />
                        </TableHead>
                        <TableHead className="w-[200px] sticky left-[40px] z-10 border-r bg-background">Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Synthetic Labour group row — rendered once when resource budget exists */}
                      {showSyntheticLabour && (
                        <Fragment key="labour-group">
                          <TableRow className="bg-muted/40">
                            <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/40">
                              <div className="flex items-center h-9">
                                <button
                                  onClick={() => setExpandedLabourCategory(isLabourGroupExpanded ? null : LABOUR_SYNTHETIC_ID)}
                                  className="p-1 hover:bg-muted rounded"
                                  title={isLabourGroupExpanded ? 'Collapse role breakdown' : 'Expand to see role breakdown'}
                                >
                                  {isLabourGroupExpanded
                                    ? <ChevronDown className="h-4 w-4" />
                                    : <ChevronRight className="h-4 w-4" />}
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/40">
                              <div className="flex items-center gap-2 h-9 px-2">
                                <span className="font-medium">{cascadeTargetDisplayName}</span>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[220px]">
                                      <p>{cascadeTargetDisplayName} revenue is calculated from the Resource Budget. To change it, revise the Resource Budget.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </TableCell>
                          </TableRow>

                          {/* Role name child rows */}
                          {isLabourGroupExpanded && (
                            labourRoles.length === 0 ? (
                              <TableRow className="bg-muted/10 border-l-2 border-l-primary/30">
                                <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/10" />
                                <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/10">
                                  <span className="text-xs text-muted-foreground italic pl-4">No roles with allocated hours</span>
                                </TableCell>
                              </TableRow>
                            ) : labourRoles.map(role => (
                              <TableRow key={`labour-role-name-${role.id}`} className="bg-muted/10 border-l-2 border-l-primary/30 h-[41px]">
                                <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-muted/10 h-[41px]" />
                                <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r bg-muted/10 h-[41px]">
                                  <Input
                                    value={`↳ ${role.role}`}
                                    readOnly
                                    className="w-full h-9 px-2 text-sm bg-transparent text-muted-foreground cursor-default border-transparent pl-6"
                                    title={role.role}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </Fragment>
                      )}
                      {/* Non-labour categories */}
                      {displayCategories.map((category) => {
                        const categoryIdx = categories.findIndex(c => c.id === category.id);
                        const isSelected = selectedRows.has(category.id);
                        const isCellSelected = selectedCell?.categoryIndex === categoryIdx;
                        return (
                          <Fragment key={category.id}>
                            <TableRow
                              className={cn(
                                'group',
                                isCellSelected && 'bg-primary/5',
                                isSelected && 'bg-primary/10',
                              )}
                            >
                              <TableCell className={cn(
                                'p-2 w-[40px] sticky left-0 z-10',
                                isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'
                              )}>
                                <div className="flex items-center h-9">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleRowSelection(category.id)}
                                    aria-label={`Select ${category.name}`}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className={cn(
                                'p-2 w-[200px] sticky left-[40px] z-10 border-r',
                                isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'
                              )}>
                                <Popover
                                    open={openCategoryPopover === categoryIdx}
                                    onOpenChange={(open) => setOpenCategoryPopover(open ? categoryIdx : null)}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openCategoryPopover === categoryIdx}
                                        className={cn(
                                          "w-full justify-between h-9",
                                          !category.name && "text-muted-foreground"
                                        )}
                                      >
                                        <span className="truncate">
                                          {category.name
                                            ? (duplicateCategoryNames.has(category.name) && category.code
                                                ? `${category.name} (${category.code})`
                                                : category.name)
                                            : "Select category"}
                                        </span>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                      <Command shouldFilter={true} filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                                        <CommandInput placeholder="Search categories..." className="h-9" />
                                        <CommandList>
                                          <CommandEmpty>No category found.</CommandEmpty>
                                          {(() => {
                                            const usedIds = displayCategories
                                              .filter(c => c.id !== category.id)
                                              .map(c => c.globalCategoryId)
                                              .filter(Boolean);
                                            const available = availableGlobalCategories.filter(gc => !usedIds.includes(gc.id) && gc.id !== cascadeTargetCategoryId);
                                            if (category.globalCategoryId && !available.some(gc => gc.id === category.globalCategoryId)) {
                                              const current = availableGlobalCategories.find(gc => gc.id === category.globalCategoryId);
                                              if (current) available.unshift(current);
                                            }
                                            const CATEGORY_TYPE_ORDER = ['labour', 'expenses', 'subcontractors', 'overhead', 'contingency'];
                                            const CATEGORY_TYPE_LABELS: Record<string, string> = {
                                              labour: 'Labour',
                                              expenses: 'Expenses',
                                              subcontractors: 'Subcontractors',
                                              overhead: 'Overhead',
                                              contingency: 'Contingency',
                                            };
                                            const groups = CATEGORY_TYPE_ORDER
                                              .map(type => ({
                                                type,
                                                label: CATEGORY_TYPE_LABELS[type] || type,
                                                items: available.filter(gc => gc.type === type),
                                              }))
                                              .filter(g => g.items.length > 0);
                                            return groups.map((group, groupIndex) => (
                                              <div key={group.type}>
                                                {groupIndex > 0 && <CommandSeparator />}
                                                <CommandGroup
                                                  heading={group.label}
                                                  className="**:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-wider **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:border-b **:[[cmdk-group-heading]]:border-border **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:mb-1"
                                                >
                                                  {group.items.map(gc => (
                                                    <CommandItem
                                                      key={gc.id}
                                                      value={`${gc.name} ${gc.code ?? ''}`}
                                                      onSelect={() => {
                                                        updateCategorySelection(categoryIdx, String(gc.id));
                                                        setOpenCategoryPopover(null);
                                                      }}
                                                      className="pl-3"
                                                    >
                                                      <div className="flex-1 min-w-0">
                                                        {gc.name}
                                                        {gc.code && (
                                                          <span className="ml-1.5 text-xs text-muted-foreground font-mono">{gc.code}</span>
                                                        )}
                                                      </div>
                                                      <Check
                                                        className={cn(
                                                          "h-4 w-4 shrink-0",
                                                          category.globalCategoryId === gc.id ? "opacity-100" : "opacity-0"
                                                        )}
                                                      />
                                                    </CommandItem>
                                                  ))}
                                                </CommandGroup>
                                              </div>
                                            ));
                                          })()}
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-background"></TableCell>
                        <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r font-semibold bg-background">Total</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* MIDDLE SECTION: Monthly Columns (Scrollable) */}
                <div 
                  className="flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)',
                  }}
                >
                  <Table 
                    className="border-collapse"
                    style={{
                      // Make table width match the number of months (100px per month)
                      width: `${yearMonths.length * 100}px`,
                      minWidth: '100%'
                    }}
                  >
                    <TableHeader>
                      <TableRow>
                        {yearMonths.map((monthData) => (
                          <TableHead key={`${monthData.year}-${monthData.month}`} className="w-[100px] text-center">
                            {MONTHS[monthData.month]}/{String(monthData.year).slice(-2)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Synthetic Labour monthly cells */}
                      {showSyntheticLabour && (
                        <Fragment key="labour-group-middle">
                          <TableRow className="bg-muted/40">
                            {visibleMonths.map((monthIndex) => (
                              <TableCell key={monthIndex} className="p-2 w-[100px] bg-muted/40">
                                <span className="flex items-center justify-center h-9 text-sm text-muted-foreground tabular-nums">
                                  {formatCurrency(labourGroupMonthly[monthIndex] || 0)}
                                </span>
                              </TableCell>
                            ))}
                          </TableRow>

                          {/* Role monthly revenue child rows */}
                          {isLabourGroupExpanded && labourRoles.map(role => (
                            <TableRow key={`labour-role-middle-${role.id}`} className="bg-muted/10 h-[41px]">
                              {visibleMonths.map((projectMonthIndex) => {
                                const ym = yearMonths[projectMonthIndex];
                                const mk = ym ? `${ym.year}-${String(ym.month + 1).padStart(2, '0')}` : '';
                                const hours = role.monthlyHours?.[mk] || 0;
                                const billRate = getRoleBillRate(role.role, role.billRate);
                                const revenue = hours * billRate;
                                return (
                                  <TableCell key={projectMonthIndex} className="p-2 w-[100px] bg-muted/10 h-[41px]">
                                    <Input
                                      value={hours > 0 ? formatCurrency(revenue) : '-'}
                                      readOnly
                                      className="w-full h-9 px-2 text-sm text-center bg-transparent text-muted-foreground cursor-default border-transparent"
                                      title={hours > 0 ? `${hours}h × ${formatCurrency(billRate)} = ${formatCurrency(revenue)}` : 'No hours allocated'}
                                    />
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </Fragment>
                      )}
                      {/* Non-labour category monthly cells */}
                      {displayCategories.map((category) => {
                        const categoryIdx = categories.findIndex(c => c.id === category.id);
                        return (
                        <TableRow key={category.id}>
                          {visibleMonths.map((monthIndex) => {
                            const existingBudgets = category.monthlyBudgets || [];
                            const value = Array(totalMonths).fill(0).map((_, i) => existingBudgets[i] || 0)[monthIndex];
                            const isSelected = selectedCell?.categoryIndex === categoryIdx && selectedCell?.monthIndex === monthIndex;
                            return (
                              <TableCell key={monthIndex} className="p-2 w-[100px]">
                                <NumberInput
                                  value={value}
                                  onChange={(newValue) => updateMonthlyBudget(categoryIdx, monthIndex, newValue)}
                                  maxValue={999999.99}
                                  onFocus={() => setSelectedCell({ categoryIndex: categoryIdx, monthIndex })}
                                  className={cn(
                                    "w-full h-9 px-2 text-sm text-center",
                                    isSelected && "ring-2 ring-ring"
                                  )}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {visibleMonths.map((monthIndex) => {
                          const labourMonthTotal = showSyntheticLabour ? (labourGroupMonthly[monthIndex] || 0) : 0;
                          const nonLabourTotal = displayCategories.reduce((sum, category) => {
                            const existingBudgets = category.monthlyBudgets || [];
                            return sum + (existingBudgets[monthIndex] || 0);
                          }, 0);
                          const monthTotal = labourMonthTotal + nonLabourTotal;
                          return (
                            <TableCell key={monthIndex} className="p-2 w-[100px]">
                              <Input
                                value={formatCurrency(monthTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted cursor-default"
                                title={formatCurrency(monthTotal)}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* RIGHT SECTION: Project Total (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-l-2">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px] text-center font-semibold bg-primary/10">Project Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Synthetic Labour total */}
                      {showSyntheticLabour && (
                        <Fragment key="labour-group-right">
                          <TableRow className="bg-muted/40">
                            <TableCell className="p-2 w-[120px] bg-muted/40">
                              <Input
                                value={formatCurrency(labourGroupTotal)}
                                readOnly
                                disabled
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted/50 text-muted-foreground cursor-not-allowed"
                                title="Labour revenue total from Resource Budget"
                              />
                            </TableCell>
                          </TableRow>

                          {/* Role total child rows */}
                          {isLabourGroupExpanded && labourRoles.map(role => {
                            const billRate = getRoleBillRate(role.role, role.billRate);
                            const roleTotal = Object.values(role.monthlyHours || {}).reduce((sum, hours) => sum + (hours * billRate), 0);
                            return (
                              <TableRow key={`labour-role-right-${role.id}`} className="bg-muted/10 h-[41px]">
                                <TableCell className="p-2 w-[120px] bg-muted/10 h-[41px]">
                                  <Input
                                    value={formatCurrency(roleTotal)}
                                    readOnly
                                    className="w-full h-9 px-2 text-sm text-center bg-transparent text-muted-foreground cursor-default border-transparent"
                                    title={`Total revenue for ${role.role}: ${formatCurrency(roleTotal)}`}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      )}
                      {/* Non-labour category totals */}
                      {displayCategories.map((category) => {
                        const categoryTotal = category.monthlyBudgets.reduce((sum, val) => sum + val, 0);
                        return (
                          <TableRow key={category.id}>
                            <TableCell className="p-2 w-[120px]">
                              <Input
                                value={formatCurrency(categoryTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
                                title={formatCurrency(categoryTotal)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[120px] bg-background">
                          <Input
                            value={formatCurrency(
                              labourGroupTotal +
                              displayCategories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0)
                            )}
                            readOnly
                            className="w-full h-9 px-2 text-sm text-center font-bold bg-primary/20 cursor-default"
                            title={formatCurrency(
                              (showSyntheticLabour ? calculateLabourRevenueFromResourceBudget().reduce((s, v) => s + v, 0) : 0) +
                              displayCategories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0)
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${formatCurrency(patternSuggestion.value)}?`}
          details={`You've entered ${formatCurrency(patternSuggestion.value)} for 3 consecutive months. Would you like to fill ${MONTHS[yearMonths[patternSuggestion.fromMonth]?.month]} through ${MONTHS[yearMonths[patternSuggestion.toMonth]?.month]} with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        onApply={handleFillApply}
        sourceMonth={fillSource?.monthIndex ?? 0}
        totalMonths={totalMonths}
        sourceValue={fillSource?.value ?? 0}
        direction="right"
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
      />

      {/* Shift Budget Sheet */}
      <ShiftBudgetSheet
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        onShift={handleShiftBudget}
        totalMonths={totalMonths}
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
        roleName={shiftSource?.categoryName ?? ''}
        monthlyHours={shiftSource ? categories[shiftSource.categoryIndex]?.monthlyBudgets ?? [] : []}
        selectedMonthIndex={selectedCell?.monthIndex ?? undefined}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteCategory()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteAttempt?.hasActuals ? 'Cannot Delete Category with Actual Costs' : 'Delete Category with Budget Values?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {deleteAttempt?.hasActuals ? (
                // Cannot delete - has actual costs
                <>
                  <p>
                    The category <strong>"{deleteAttempt?.category.name}"</strong> cannot be deleted because it has actual costs recorded.
                  </p>
                  <p className="text-sm">
                    To remove this category from your project:
                  </p>
                  <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                    <li>Ensure there are no actual costs recorded</li>
                    {deleteAttempt?.hasBudget && <li>Clear all monthly budget values (set them to zero)</li>}
                    <li>Then try deleting the category again</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-2">
                    This prevents accidental deletion of categories with actual financial data.
                  </p>
                </>
              ) : (
                // Warning - has budget values but can delete
                <>
                  <p>
                    The category <strong>"{deleteAttempt?.category.name}"</strong> has budget values allocated.
                  </p>
                  <p className="text-sm text-warning-600 dark:text-warning-400 font-medium">
                    ⚠️ Are you sure you want to delete this category?
                  </p>
                  <p className="text-sm">
                    Deleting this category will remove all budget values. This action cannot be undone.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    If you're unsure, you can cancel and clear the budget values manually first.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {deleteAttempt?.hasActuals ? (
              // Only "I Understand" button when there are actuals
              <AlertDialogAction onClick={cancelDeleteCategory}>
                I Understand
              </AlertDialogAction>
            ) : (
              // Cancel and Delete buttons when only budget values
              <>
                <AlertDialogCancel onClick={cancelDeleteCategory}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={confirmDeleteCategory} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete Category
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SalesCostCentreBudgetView
//
// Renders cost centres as rows with editable monthly revenue budget inputs.
// Mirrors the layout of the Cost Centres tab (flat list or structure/hierarchical
// view) but replaces the read-only financial columns with monthly input cells.
//
// Each cost centre has one budget value per month (not per category).
// Budget values are persisted to localStorage keyed by projectId + ccCode.
// ─────────────────────────────────────────────────────────────────────────────

// Build ordered list of months between project start and end dates
function buildProjectMonths(startDate?: string, endDate?: string): { year: number; month: number; key: string }[] {
  if (!startDate || !endDate) {
    const y = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => ({
      year: y,
      month: i,
      key: `${y}-${String(i + 1).padStart(2, '0')}`,
    }));
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  const months: { year: number; month: number; key: string }[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    months.push({
      year: cur.getFullYear(),
      month: cur.getMonth(),
      key: `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`,
    });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

interface SalesCostCentreBudgetViewProps {
  costCentres: CostCentre[];
  hierarchicalCostCentres: HierarchicalCostCentre[];
  isStructureView: boolean;
  projectId: string;
  projectStartDate?: string;
  projectEndDate?: string;
  onChange: (changes: SalesBudget) => void;
  salesBudget: SalesBudget;
}

// Flatten a hierarchical tree into an ordered list with depth level attached
interface FlatCcNode extends CostCentre {
  level: number;
  hasChildren: boolean;
  parentId?: number | null;
}

/** Build a proper tree from a flat array with parentId references (mirrors CostCentresHierarchicalTable) */
function buildTree(items: HierarchicalCostCentre[]): HierarchicalCostCentre[] {
  const map = new Map<number, HierarchicalCostCentre>();
  const roots: HierarchicalCostCentre[] = [];

  items.forEach(item => {
    map.set(item.id, { ...item, children: [], level: 0 });
  });

  items.forEach(item => {
    const node = map.get(item.id)!;
    if (item.parentId !== undefined && item.parentId !== null && map.has(item.parentId)) {
      const parent = map.get(item.parentId)!;
      if (!parent.children) parent.children = [];
      parent.children.push(node);
      node.level = (parent.level || 0) + 1;
    } else {
      roots.push(node);
    }
  });

  return roots;
}

function flattenHierarchy(nodes: HierarchicalCostCentre[], level = 0): FlatCcNode[] {
  const result: FlatCcNode[] = [];
  for (const node of nodes) {
    result.push({
      ...node,
      level,
      hasChildren: !!(node.children && node.children.length > 0),
    });
    if (node.children && node.children.length > 0) {
      result.push(...flattenHierarchy(node.children, level + 1));
    }
  }
  return result;
}

function SalesCostCentreBudgetView({
  costCentres,
  hierarchicalCostCentres,
  isStructureView,
  projectId,
  projectStartDate,
  projectEndDate,
  onChange,
  salesBudget,
}: SalesCostCentreBudgetViewProps) {

  const months = useMemo(
    () => buildProjectMonths(projectStartDate, projectEndDate),
    [projectStartDate, projectEndDate]
  );

  // Per-CC monthly budget values: ccCode → Record<"YYYY-MM", number>
  // When the parent passes costCentreBudgets (e.g. via undo/redo), use those
  // values instead of localStorage so the restored state is accurate.
  const [ccBudgets, setCcBudgets] = useState<Record<string, Record<string, number>>>(() => {
    if (salesBudget.costCentreBudgets && Object.keys(salesBudget.costCentreBudgets).length > 0) {
      return { ...salesBudget.costCentreBudgets };
    }
    const allCcs = isStructureView ? flattenHierarchy(buildTree(hierarchicalCostCentres)) : costCentres;
    const initial: Record<string, Record<string, number>> = {};
    for (const cc of allCcs) {
      const saved = loadCcSalesBudget(projectId, cc.code);
      initial[cc.code] = saved ? { ...saved.monthlyBudgets } : {};
    }
    return initial;
  });

  // Fetch CC sales budgets from API on mount and update state
  const apiFetchedRef = useRef(false);
  useEffect(() => {
    if (apiFetchedRef.current) return;
    apiFetchedRef.current = true;
    const numericPid = parseInt(projectId, 10);
    if (!numericPid) return;
    fetchCostCentreBudgetsByProject(numericPid, 'sales')
      .then((entries) => {
        if (!entries || entries.length === 0) return;
        setCcBudgets((prev) => {
          const updated = { ...prev };
          for (const entry of entries) {
            const cc = costCentres.find(c => c.id === entry.costCentreId);
            const code = entry.costCentreCode || cc?.code;
            if (code && entry.monthlyBudgets) {
              updated[code] = entry.monthlyBudgets;
              saveCcSalesBudget(projectId, code, { monthlyBudgets: entry.monthlyBudgets });
            }
          }
          return updated;
        });
      })
      .catch((err) => {
        console.error('[SalesBudget] Failed to fetch CC budgets from API:', err);
      });
  }, [projectId, costCentres]);

  const isInitialMount = useRef(true);

  // Propagate changes on every edit (API persist deferred to Save Budget)
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    // Update localStorage cache for immediate UI consistency on re-open
    for (const [code, monthly] of Object.entries(ccBudgets)) {
      saveCcSalesBudget(projectId, code, { monthlyBudgets: monthly });
    }
    // Build pending CC budget entries for the save flow
    const pendingCcBudgets = Object.entries(ccBudgets)
      .map(([code, monthly]) => {
        const cc = costCentres.find(c => c.code === code);
        return cc ? { costCentreId: cc.id, budgetType: 'sales' as const, monthlyBudgets: monthly } : null;
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);
    // Sum all months across all CCs for the totals payload
    const totalBudget = Object.values(ccBudgets).reduce(
      (sum, monthly) => sum + Object.values(monthly).reduce((s, v) => s + v, 0),
      0
    );
    onChange({
      ...salesBudget,
      projectType: 'time-and-materials',
      costCentreBudgets: ccBudgets,
      totals: {
        budget: totalBudget,
        actual: salesBudget.totals?.actual ?? 0,
        remaining: totalBudget - (salesBudget.totals?.actual ?? 0),
        percentBilled: totalBudget > 0 ? ((salesBudget.totals?.actual ?? 0) / totalBudget) * 100 : 0,
      },
      pendingCcBudgets,
    });
  }, [ccBudgets]);

  // ── Cell selection (for Fill / Shift toolbar buttons) ──────────────────
  const [selectedCell, setSelectedCell] = useState<{ ccCode: string; monthKey: string } | null>(null);

  // ── Fill state ──────────────────────────────────────────────────────────
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ ccCode: string; monthIndex: number; value: number } | null>(null);

  // ── Shift state ─────────────────────────────────────────────────────────
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ ccCode: string; ccName: string } | null>(null);

  // ── Pattern detection state ─────────────────────────────────────────────
  const [patternSuggestion, setPatternSuggestion] = useState<{
    ccCode: string;
    value: number;
    fromMonthIndex: number;
    toMonthIndex: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  const detectPattern = (ccCode: string, monthIndex: number, value: number, currentBudgets: Record<string, number>) => {
    const suggestionKey = `pattern-${ccCode}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    if (monthIndex >= 2 && value > 0) {
      const prev2 = currentBudgets[months[monthIndex - 2]?.key] ?? 0;
      const prev1 = currentBudgets[months[monthIndex - 1]?.key] ?? 0;
      if (prev2 === value && prev1 === value && monthIndex < months.length - 1) {
        setPatternSuggestion({ ccCode, value, fromMonthIndex: monthIndex + 1, toMonthIndex: months.length - 1 });
      }
    }
  };

  const updateCell = (ccCode: string, monthKey: string, value: number) => {
    setCcBudgets(prev => {
      const updated = { ...prev, [ccCode]: { ...(prev[ccCode] ?? {}), [monthKey]: value } };
      const monthIndex = months.findIndex(m => m.key === monthKey);
      if (monthIndex !== -1) detectPattern(ccCode, monthIndex, value, updated[ccCode]);
      return updated;
    });
  };

  // ── Fill handlers ───────────────────────────────────────────────────────
  const handleFillClick = (ccCode: string, monthIndex: number) => {
    const value = ccBudgets[ccCode]?.[months[monthIndex].key] ?? 0;
    setFillSource({ ccCode, monthIndex, value });
    setFillDialogOpen(true);
  };

  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;
    const { ccCode, monthIndex, value } = fillSource;
    setCcBudgets(prev => {
      const existing = { ...(prev[ccCode] ?? {}) };
      const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
      const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
      const count = Math.abs(endMonth - startMonth) + 1;
      if (fillType === 'copy') {
        for (let i = startMonth; i <= endMonth; i++) existing[months[i].key] = value;
      } else if (fillType === 'distribute') {
        const perMonth = Math.floor((value / count) * 100) / 100;
        const remainder = value - perMonth * count;
        for (let i = startMonth; i <= endMonth; i++) {
          existing[months[i].key] = i === endMonth ? perMonth + remainder : perMonth;
        }
      }
      return { ...prev, [ccCode]: existing };
    });
    setFillDialogOpen(false);
    setFillSource(null);
  };

  // ── Shift handlers ──────────────────────────────────────────────────────
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;
    const { ccCode } = shiftSource;
    setCcBudgets(prev => {
      const existing = { ...(prev[ccCode] ?? {}) };
      const shiftAmount = destinationStart - sourceFrom;
      const original = { ...existing };
      for (let i = sourceFrom; i <= sourceTo; i++) {
        const dest = i + shiftAmount;
        if (dest >= 0 && dest < months.length) existing[months[dest].key] = original[months[i].key] ?? 0;
      }
      if (clearSource) {
        const destEnd = sourceTo + shiftAmount;
        for (let i = sourceFrom; i <= sourceTo; i++) {
          if (i < destinationStart || i > destEnd) existing[months[i].key] = 0;
        }
      }
      return { ...prev, [ccCode]: existing };
    });
    setShiftDialogOpen(false);
    setShiftSource(null);
  };

  // ── Pattern accept / dismiss ────────────────────────────────────────────
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    const { ccCode, value, fromMonthIndex, toMonthIndex } = patternSuggestion;
    setCcBudgets(prev => {
      const existing = { ...(prev[ccCode] ?? {}) };
      for (let i = fromMonthIndex; i <= toMonthIndex; i++) existing[months[i].key] = value;
      return { ...prev, [ccCode]: existing };
    });
    setPatternSuggestion(null);
  };

  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      setDismissedSuggestions(prev => new Set(prev).add(`pattern-${patternSuggestion.ccCode}`));
    }
    setPatternSuggestion(null);
  };

  // Determine which rows to render
  const flatRows = useMemo<FlatCcNode[]>(() => {
    if (isStructureView) return flattenHierarchy(buildTree(hierarchicalCostCentres));
    return costCentres.map(cc => ({ ...cc, level: 0, hasChildren: false }));
  }, [isStructureView, costCentres, hierarchicalCostCentres]);

  // Track which parent nodes are expanded (structure view only) — all expanded by default
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(() =>
    new Set(flatRows.filter(r => r.hasChildren).map(r => r.id))
  );

  const toggleNode = (id: number) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllNodes = () =>
    setExpandedNodes(new Set(flatRows.filter(r => r.hasChildren).map(r => r.id)));

  const collapseAllNodes = () => setExpandedNodes(new Set());

  // Only show rows whose ancestors are all expanded
  const visibleRows = useMemo(() => {
    if (!isStructureView) return flatRows;
    // Build parent map: childId → parentId
    const parentMap = new Map<number, number | null>();
    for (const row of flatRows) {
      parentMap.set(row.id, row.parentId ?? null);
    }
    return flatRows.filter(row => {
      // Walk up ancestors — if any is collapsed, hide this row
      let pid = row.parentId ?? null;
      while (pid !== null && pid !== undefined) {
        if (!expandedNodes.has(pid)) return false;
        pid = parentMap.get(pid) ?? null;
      }
      return true;
    });
  }, [flatRows, expandedNodes, isStructureView]);

  if (flatRows.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground space-y-2">
        <p className="font-medium">No cost centres found for this project.</p>
        <p className="text-sm">Add cost centres on the Cost Centres tab before entering revenue budgets in Cost Centre mode.</p>
      </div>
    );
  }

  // Column totals — only over leaf (non-parent) rows so parents don't double-count
  const leafRows = isStructureView ? flatRows.filter(r => !r.hasChildren) : flatRows;
  const colTotals: Record<string, number> = {};
  for (const { key } of months) {
    colTotals[key] = leafRows.reduce((s, cc) => s + (ccBudgets[cc.code]?.[key] ?? 0), 0);
  }
  const grandTotal = Object.values(colTotals).reduce((s, v) => s + v, 0);

  // Derive selectedCell month index for toolbar button states
  const selectedMonthIndex = selectedCell
    ? months.findIndex(m => m.key === selectedCell.monthKey)
    : -1;
  const selectedValue = selectedCell
    ? (ccBudgets[selectedCell.ccCode]?.[selectedCell.monthKey] ?? 0)
    : 0;
  const fillEnabled = selectedCell !== null && selectedValue > 0 && selectedMonthIndex < months.length - 1;
  const shiftEnabled = selectedCell !== null && selectedValue > 0;

  return (
    <div className="-mx-6 space-y-4">
    <div className="overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Sales Budget by Cost Centre</h2>
          {isStructureView && (
            <>
              <Button size="sm" variant="outline" onClick={expandAllNodes}>
                Expand All
              </Button>
              <Button size="sm" variant="outline" onClick={collapseAllNodes}>
                Collapse All
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!fillEnabled}
            title="Fill right from selected cell"
            onClick={() => {
              if (selectedCell) handleFillClick(selectedCell.ccCode, selectedMonthIndex);
            }}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Fill
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!shiftEnabled}
            title="Shift budget between months (select a monthly cell first)"
            onClick={() => {
              if (selectedCell) {
                const cc = flatRows.find(r => r.code === selectedCell.ccCode);
                setShiftSource({ ccCode: selectedCell.ccCode, ccName: cc?.name ?? selectedCell.ccCode });
                setShiftDialogOpen(true);
              }
            }}
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Shift
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {/* Identification */}
              <TableHead className="sticky left-0 z-20 bg-muted/50 min-w-[90px] border-r text-xs font-semibold">Code</TableHead>
              <TableHead className="sticky left-[90px] z-20 bg-muted/50 min-w-[200px] border-r text-xs font-semibold">Name</TableHead>
              {/* Monthly inputs */}
              {months.map(({ year, month, key }) => (
                <TableHead key={key} className="min-w-[110px] text-right text-xs font-semibold px-2">
                  {MONTHS[month]} {year}
                </TableHead>
              ))}
              {/* Row total */}
              <TableHead className="min-w-[110px] text-right text-xs font-semibold px-2 border-l">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((cc) => {
              const isParent = cc.hasChildren;
              const isExpanded = expandedNodes.has(cc.id);
              const monthly = ccBudgets[cc.code] ?? {};
              // For parent rows in structure view, sum children's values
              const rowTotal = isParent && isStructureView
                ? flatRows
                    .filter(r => !r.hasChildren && r.parentId === cc.id)
                    .reduce((s, child) => s + months.reduce((ms, { key }) => ms + (ccBudgets[child.code]?.[key] ?? 0), 0), 0)
                : months.reduce((s, { key }) => s + (monthly[key] ?? 0), 0);
              const indentWidth = cc.level * 24;
              const rowBg = isParent ? 'bg-muted/50' : 'bg-background';
              return (
                <TableRow key={`${cc.id}-${cc.code}`} className={cn('group', isParent && 'border-t border-b border-border/60')}>
                  {/* Code with chevron for parents */}
                  <TableCell className={cn('sticky left-0 z-10 border-r px-1 py-2 text-sm', rowBg, `group-hover:${isParent ? 'bg-muted/70' : 'bg-muted/30'}`)}>
                    <div className="flex items-center gap-1">
                      {isParent ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-muted shrink-0"
                          onClick={() => toggleNode(cc.id)}
                        >
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      ) : (
                        <div className="w-6 shrink-0" />
                      )}
                      <span className={cn(isParent ? 'font-bold text-foreground' : 'text-muted-foreground')}>{cc.code}</span>
                    </div>
                  </TableCell>
                  {/* Name with indent + folder icon for parents */}
                  <TableCell className={cn('sticky left-[90px] z-10 border-r py-2 text-sm', rowBg, `group-hover:${isParent ? 'bg-muted/70' : 'bg-muted/30'}`)}>
                    <div className="flex items-center gap-1" style={{ paddingLeft: `${indentWidth}px` }}>
                      {isParent
                        ? isExpanded
                          ? <FolderOpen className="h-4 w-4 text-info-500 shrink-0" />
                          : <Folder className="h-4 w-4 text-info-500 shrink-0" />
                        : null}
                      <span className={cn('font-medium truncate', isParent ? 'text-foreground' : 'text-foreground/80')}>
                        {cc.name}
                      </span>
                    </div>
                  </TableCell>
                  {/* Monthly cells */}
                  {months.map(({ key }) => (
                    <TableCell key={key} className="px-1 py-1 text-right">
                      {isParent ? (
                        <span className="text-sm tabular-nums text-muted-foreground px-3">
                          {formatCurrency(flatRows
                            .filter(r => !r.hasChildren && r.parentId === cc.id)
                            .reduce((s, child) => s + (ccBudgets[child.code]?.[key] ?? 0), 0))}
                        </span>
                      ) : (
                        <NumberInput
                          value={monthly[key] ?? 0}
                          onChange={v => updateCell(cc.code, key, v)}
                          onFocus={() => setSelectedCell({ ccCode: cc.code, monthKey: key })}
                          className={cn(
                            "h-8 text-sm text-right tabular-nums w-full min-w-[100px]",
                            selectedCell?.ccCode === cc.code && selectedCell?.monthKey === key && "ring-2 ring-ring"
                          )}
                        />
                      )}
                    </TableCell>
                  ))}
                  {/* Row total */}
                  <TableCell className={cn('px-3 py-2 text-right text-sm font-semibold tabular-nums border-l', isParent ? 'text-foreground' : 'text-muted-foreground')}>
                    {formatCurrency(rowTotal)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell className="sticky left-0 z-10 bg-muted/50 border-r px-3 py-2 text-xs" colSpan={2}>
                Total
              </TableCell>
              {months.map(({ key }) => (
                <TableCell key={key} className="px-3 py-2 text-right text-sm tabular-nums">
                  {formatCurrency(colTotals[key] ?? 0)}
                </TableCell>
              ))}
              <TableCell className="px-3 py-2 text-right text-sm tabular-nums border-l">
                {formatCurrency(grandTotal)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${formatCurrency(patternSuggestion.value)}?`}
          details={`You've entered ${formatCurrency(patternSuggestion.value)} for 3 consecutive months. Would you like to fill the remaining months with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        onApply={handleFillApply}
        sourceMonth={fillSource?.monthIndex ?? 0}
        totalMonths={months.length}
        sourceValue={fillSource?.value ?? 0}
        direction="right"
        monthLabels={months.map(({ year, month }) => `${MONTHS[month]} ${year}`)}
      />

      {/* Shift Budget Sheet */}
      <ShiftBudgetSheet
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        onShift={handleShiftBudget}
        totalMonths={months.length}
        monthLabels={months.map(({ year, month }) => `${MONTHS[month]} ${year}`)}
        roleName={shiftSource?.ccName ?? ''}
        monthlyHours={shiftSource ? months.map(m => ccBudgets[shiftSource.ccCode]?.[m.key] ?? 0) : []}
        selectedMonthIndex={selectedMonthIndex >= 0 ? selectedMonthIndex : undefined}
      />
    </div>
  );
}
